import type { CreateInvoiceForm, CreateInvoiceRequest } from '../Types/createInvoiceFormTypes';
import type { InvoiceSellerSectionFormSlice } from '../Components/InvoiceSellerSection/InvoiceSellerSection.types';
import { getVatRate, isVatExempt } from './vatMappings';
import { VatRateType } from '../Types/invoicesTypes';
import { today, plusDays, normalizeDate } from '../../Shared/Helpers/dateHelpers';
import { invoiceLogger } from './invoiceLogger';

// Re-export for backward compatibility
export { today, plusDays, normalizeDate };

export const DEFAULT_INVOICE_TEMPLATE_VARIANT = 'modern' as const;

export const normalizeInvoiceTemplateVariant = (value?: string | null): string =>
  value === DEFAULT_INVOICE_TEMPLATE_VARIANT ? value : DEFAULT_INVOICE_TEMPLATE_VARIANT;

export const termOf = (from?: string, to?: string) => {
  const f = normalizeDate(from);
  const t = normalizeDate(to);
  return f && t ? `${f} – ${t}` : f || t;
};

const buildTourismParticipationLabel = (offerName?: string, dateFrom?: string, dateTo?: string) => {
  const normalizedOfferName = offerName?.trim();
  const normalizeReservationLabelDate = (value?: string) => {
    if (!value) {
      return undefined;
    }

    const normalized = value.slice(0, 10);
    return normalized && normalized !== '0001-01-01' ? normalized : undefined;
  };

  const normalizedFrom = normalizeReservationLabelDate(normalizeDate(dateFrom));
  const normalizedTo = normalizeReservationLabelDate(normalizeDate(dateTo));

  let details = '';
  if (normalizedOfferName) {
    details = normalizedOfferName;
  }

  if (normalizedFrom && normalizedTo) {
    details = details ? `${details} od ${normalizedFrom} do ${normalizedTo}` : `od ${normalizedFrom} do ${normalizedTo}`;
  } else if (normalizedFrom) {
    details = details ? `${details} od ${normalizedFrom}` : `od ${normalizedFrom}`;
  } else if (normalizedTo) {
    details = details ? `${details} do ${normalizedTo}` : `do ${normalizedTo}`;
  }

  return details
    ? `Uczestnictwo w imprezie turystycznej: ${details}`
    : 'Uczestnictwo w imprezie turystycznej';
};

/**
 * Formats invoice item name from reservation details
 * Returns standardized tourism participation label.
 */
export const formatInvoiceItemName = (reservationDetails?: any): string => {
  invoiceLogger.debug('[formatInvoiceItemName] Input:', {
    hasReservationDetails: !!reservationDetails,
    hasBooking: !!reservationDetails?.booking,
    rootOfferName: reservationDetails?.offerName,
    offerName: reservationDetails?.booking?.offerName,
    rootBookingDateFrom: reservationDetails?.bookingDateFrom,
    rootBookingDateTo: reservationDetails?.bookingDateTo,
    rootDateFrom: reservationDetails?.dateFrom,
    rootDateTo: reservationDetails?.dateTo,
    dateFrom: reservationDetails?.booking?.bookingDateFrom,
    dateTo: reservationDetails?.booking?.bookingDateTo,
    bookingDateFromFallback: reservationDetails?.booking?.dateFrom,
    bookingDateToFallback: reservationDetails?.booking?.dateTo,
  });

  const offerName = reservationDetails?.booking?.offerName || reservationDetails?.offerName;
  const dateFrom =
    reservationDetails?.booking?.bookingDateFrom ||
    reservationDetails?.booking?.dateFrom ||
    reservationDetails?.bookingDateFrom ||
    reservationDetails?.dateFrom;
  const dateTo =
    reservationDetails?.booking?.bookingDateTo ||
    reservationDetails?.booking?.dateTo ||
    reservationDetails?.bookingDateTo ||
    reservationDetails?.dateTo;

  const result = buildTourismParticipationLabel(offerName, dateFrom, dateTo);
  invoiceLogger.debug('[formatInvoiceItemName] Result:', result);
  return result;
};

// Formatting helpers
export const composeAddress = (streetNo?: string, rest?: string) =>
  [streetNo, rest].filter(Boolean).join(', ');

// Value coercion
export const toNumber = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0));
export const toBool = (v: unknown) => (typeof v === 'boolean' ? v : String(v) === 'true');

// NIP cleaning - remove dashes and spaces
export const cleanNip = (nip?: string | null) => {
  if (!nip) return '';
  return nip.replace(/[-\s]/g, '');
};

const isCountryIso = (value?: string | null): boolean => !!value && /^[A-Za-z]{2}$/.test(value);

const deriveCountryIsoFromNip = (nip?: string | null): string | undefined => {
  const cleaned = cleanNip(nip).toUpperCase();
  if (/^[A-Z]{2}\d{2,}$/.test(cleaned)) {
    return cleaned.slice(0, 2);
  }

  return undefined;
};

const resolveBuyerCountryIso = (form: CreateInvoiceForm): string => {
  const fromForm = (form.buyerCountryIso || '').trim().toUpperCase();
  if (isCountryIso(fromForm)) {
    return fromForm;
  }

  const fromNip = deriveCountryIsoFromNip(form.buyerNip);
  if (fromNip && isCountryIso(fromNip)) {
    return fromNip;
  }

  return 'PL';
};

const resolveIsBuyerVatEu = (form: CreateInvoiceForm, buyerCountryIso: string): boolean => {
  if (buyerCountryIso === 'PL') {
    return false;
  }

  if (typeof form.isBuyerVatEu === 'boolean') {
    return form.isBuyerVatEu;
  }

  const fromNip = deriveCountryIsoFromNip(form.buyerNip);
  return !!fromNip && fromNip !== 'PL';
};

// Enum normalization: send string enum names (camelCase) to API
const InvoiceTypeList = [
  'Standard',
  'Prepayment',
  'Final',
  'Correction',
  'Refund',
  'Proforma',
] as const;
const InvoiceSchemeList = ['Standard', 'TravelMargin'] as const;
const PaymentMethodTypeList = [
  'Transfer',
  'Cash',
  'Card',
  'Online',
  'SplitPayment',
  'Voucher',
  'Credit',
  'BankOrder',
  'Cheque',
  'Other',
] as const;
const VatRateTypeList = [
  'Vat23',
  'Vat8',
  'Vat5',
  'VatZW', // Changed from Vat0
  'VatExempt',
  'VatNP', // Changed from VatNp
  'VatEU', // NEW
  'VatMargin',
  'VatRehab', // NEW
] as const;

const toCamelCaseName = (s: string) => (s.length ? s[0].toLowerCase() + s.slice(1) : s);
const normalizeKey = (s: string) => s.replace(/[^a-z0-9]/gi, '').toLowerCase();

/**
 * Picks enum name from list by value (supports number index or string match)
 * @param value - Enum value (number or string)
 * @param list - List of enum names
 * @returns Enum name or undefined
 */
function pickEnumName(value: unknown, list: readonly string[]): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const idx = value as number;
    return list[idx] ?? undefined;
  }
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) {
    const idx = Number(raw);
    return list[idx] ?? undefined;
  }
  const target = normalizeKey(raw);
  const found = list.find((n) => normalizeKey(n) === target);
  return found ?? raw; // fallback to raw string
}

/**
 * Converts enum value to camelCase string (backend API format)
 * @param value - Enum value (number or string)
 * @param list - List of enum names
 * @returns camelCase string or undefined
 */
function asEnumString(value: unknown, list: readonly string[]): string | undefined {
  const name = pickEnumName(value, list);
  return name ? toCamelCaseName(name) : undefined;
}

/**
 * Builds API payload for CreateInvoice endpoint
 * - Maps form data to CreateInvoiceRequest
 * - Handles enum conversion to camelCase strings
 * - Computes VAT fields (rate, exemption) per item
 * - Supports legacy seller slice for backward compatibility
 * @param form - Invoice form data
 * @param seller - Optional legacy seller data
 * @returns API-ready CreateInvoiceRequest payload
 */
export const buildInvoicePayload = (
  form: CreateInvoiceForm,
  seller?: InvoiceSellerSectionFormSlice
): CreateInvoiceRequest => {
  invoiceLogger.debug('[buildInvoicePayload] Building payload...', {
    type: form.type,
    itemsCount: form.items?.length,
  });

  const composedBuyerAddress = composeAddress(form.buyerStreetNo, form.buyerAddress);
  const composedRecipientAddress = form.useDifferentRecipient
    ? composeAddress(form.recipientStreetNo, form.recipientAddress)
    : undefined;
  const buyerCountryIso = resolveBuyerCountryIso(form);
  const isBuyerVatEu = resolveIsBuyerVatEu(form, buyerCountryIso);
  // Seller from form or legacy seller slice (backward compatibility)
  const composedSellerAddress = composeAddress(
    form.sellerStreetNo || seller?.sellerStreetNo || '',
    form.sellerAddress || seller?.sellerAddress || ''
  );
  const invoiceTypeName = pickEnumName(form.type, InvoiceTypeList);
  const isPrepaymentInvoice = invoiceTypeName === 'Prepayment';

  const payload = {
    // --- Typ i powiązania ---
    reservationId: form.reservationId,
    companyId: form.companyId, // NEW: Seller company ID
    ...(form.reservationId
      ? { useReservationPaymentImport: form.useReservationPaymentImport, useReservationInvoiceDefaults: false }
      : {}),
    // send enums as strings (camelCase)
    type: asEnumString(form.type, InvoiceTypeList) as any,
    scheme: asEnumString(form.scheme, InvoiceSchemeList) as any,
    currency: form.currency,
    // Correction invoice reference (only for type=Correction)
    correctionInvoiceId: form.correctionInvoiceId || undefined,

    // --- Daty i miejsce ---
    issueDate: normalizeDate(form.issueDate),
    saleDate: normalizeDate(form.saleDate),
    placeOfIssue: form.placeOfIssue,

    // --- Płatność ---
    paymentMethod: asEnumString(form.paymentMethod, PaymentMethodTypeList) as any,
    paymentDueDate: normalizeDate(form.paymentDueDate),

    // --- Payment fields (required for Prepayment) ---
    status: form.status as any,
    // When useReservationPaymentImport=true, do NOT send paidAmount — backend resolves it
    // from reservation payment history via ReservationPaymentImportResolver. Sending paidAmount
    // here would trigger HasExplicitPaymentOverride and bypass ImportedReservationPaymentAmount
    // tracking, allowing the same payments to be settled on multiple invoices.
    // Exception: Prepayment invoices must always carry explicit PaidAmount > 0.
    paidAmount:
      form.useReservationPaymentImport && !isPrepaymentInvoice ? undefined : form.paidAmount,
    // ✅ paidAt expects DateTime (ISO 8601 with time), not DateOnly
    // If form sends "YYYY-MM-DD", append "T00:00:00Z" for midnight UTC
    paidAt: form.paidAt
      ? form.paidAt.includes('T')
        ? form.paidAt
        : `${form.paidAt}T00:00:00Z`
      : undefined,
    paymentComment: form.paymentComment || undefined, // Backend: PaymentStatusHelper auto-fills for Cash/Prepayment

    // --- Sprzedawca (wymagane przez CreateInvoiceCommand) ---
    sellerName: form.sellerName || seller?.sellerName || '',
    sellerAddress: composedSellerAddress,
    sellerNip: cleanNip(form.sellerNip || seller?.sellerNip),
    sellerBankAccount: form.bankAccount || undefined, // UWAGA: nazwa zgodna z CreateInvoiceCommand

    // --- Nabywca (wymagane) ---
    buyerName: form.buyerName,
    buyerAddress: composedBuyerAddress,
    buyerNip: cleanNip(form.buyerNip) || undefined,
    buyerCountryIso,
    isBuyerVatEu,
    buyerIsJst: form.buyerIsJst,

    // --- Odbiorca (opcjonalnie) ---
    recipientName: form.useDifferentRecipient ? form.recipientName : undefined,
    recipientAddress: composedRecipientAddress,
    recipientNip: form.useDifferentRecipient ? cleanNip(form.recipientNip) : undefined,

    // --- Inne ---
    comments: form.comments,

    // --- PDF Template Variant ---
    templateVariant: normalizeInvoiceTemplateVariant(form.templateVariant),

    // --- PFRON Refund fields ---
    refundParticipantReferralCost: form.refundParticipantReferralCost,
    refundGuardianReferralCost: form.refundGuardianReferralCost,
    refundPfronSubsidyParticipant: form.refundPfronSubsidyParticipant,
    refundPfronSubsidyGuardian: form.refundPfronSubsidyGuardian,
    refundOwnContribution: form.refundOwnContribution,

    // --- Pozycje ---
    items: form.items.map((i) => {
      const vatType = i.vatType as VatRateType;
      const computedVatRate = getVatRate(vatType);
      const computedIsVatExempt = isVatExempt(vatType);

      return {
        name: i.name,
        description: i.description || '',
        quantity: toNumber(i.quantity),
        unitNet: toNumber(i.unitNet),
        unitGross: toNumber(i.unitGross),
        vatRate: computedVatRate,
        vatType: asEnumString(i.vatType, VatRateTypeList) as any,
        isVatExempt: computedIsVatExempt,
        vatExemptNote: computedIsVatExempt ? i.vatExemptNote || '' : '',
      };
    }),
  };

  invoiceLogger.debug('[buildInvoicePayload] ✅ Payload built:', {
    type: payload.type,
    scheme: payload.scheme,
    itemsCount: payload.items.length,
  });

  return payload;
};
